import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { PanelSearch } from '../PanelSearch'
import type { Fan, FanList } from './collectionsData'

interface Props {
  title: string
  lists: FanList[]
  fans: Fan[]
  /** The list on screen: adding to it would change nothing. */
  exceptId?: string
  onAdd: (lists: FanList[]) => void
  onClose: () => void
}

/** Past this many lists the menu gets a search box. */
const SEARCH_FROM = 8

export function alreadyIn(fans: Fan[], listId: string): number {
  return fans.filter((f) => f.lists.some((s) => s.id === listId && s.hasUser)).length
}

/**
 * "Add N fans to": tick any number of the lists OnlyFans accepts fans into
 * (each shows how many are already there), then Add once.
 */
export function ListMenu({ title, lists, fans, exceptId, onAdd, onClose }: Props) {
  const { t } = useTranslation('library')
  const [picked, setPicked] = useState<string[]>([])
  const [query, setQuery] = useState('')
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const options = lists.filter((l) => l.canAddUsers && l.id !== exceptId)
  const q = query.trim().toLowerCase()
  const shown = q ? options.filter((l) => l.name.toLowerCase().includes(q)) : options
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))
  const add = () => picked.length && onAdd(options.filter((l) => picked.includes(l.id)))
  return (
    <>
      <div className="lib-backdrop" onClick={onClose} />
      <div className="lib-pop lib-menu lib-listpick" role="dialog" aria-label={title}>
        <span className="lib-menu-head">{title}</span>
        {options.length > SEARCH_FROM && (
          <div className="lib-listpick-search">
            <PanelSearch label={t('collections.searchLists')} value={query} onChange={setQuery} />
          </div>
        )}
        <div className="lib-listpick-rows" role="group" aria-label={title}>
          {shown.map((l) => {
            const have = alreadyIn(fans, l.id)
            const all = have === fans.length
            const on = picked.includes(l.id)
            return (
              <button
                key={l.id}
                type="button"
                role="checkbox"
                aria-checked={on}
                disabled={all}
                className={`lib-menu-item ${on ? 'is-on' : ''}`}
                onClick={() => toggle(l.id)}
              >
                <span className="lib-listpick-box" aria-hidden="true">
                  {(on || all) && <FeatherIcon name="check" size={11} />}
                </span>
                <span className="lib-menu-label">{l.name}</span>
                <span className={`lib-menu-meta ${all ? 'is-on' : ''}`}>
                  {all ? t('alreadyIn') : have ? t('someAlreadyIn', { count: have }) : l.users.toLocaleString()}
                </span>
              </button>
            )
          })}
          {!shown.length && <span className="lib-listpick-empty">{t('collections.noListsMatch')}</span>}
        </div>
        <div className="lib-confirm-actions lib-listpick-foot">
          <button type="button" className="lib-btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="lib-btn lib-btn--primary" disabled={!picked.length} onClick={add}>
            {picked.length ? t('collections.addToN', { count: picked.length }) : t('collections.add')}
          </button>
        </div>
      </div>
    </>
  )
}
